// Barra flutuante (~300 × 48, P0.4 / Figma 3.5): nome cortado com "…" + timer;
// no hover: pausar/retomar, finalizar e abrir a janela principal. Arrastável
// (data-tauri-drag-region). Ponto vermelho quando offline. Ao concluir: meme + confetes — ou,
// com "Comemoração" desligada, só "Feito!" verde no lugar do título por 0,5 s (AC-230).
// Ao iniciar uma tarefa, "Iniciada" em verde ao lado do título por 2 s e sai para cima.
// Hora de relaxar do Pomodoro: a barra crescida mostra o tomate relaxando e "Vamos relaxar";
// se havia comemoração, o meme sai para a esquerda e dá lugar a ela (AC-189).
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pomodoroView } from '../domain/pomodoro';
import { formatDuration } from '../i18n/format';
import { timerView } from '../domain/timer';
import type { FloatCommand, FloatSnapshot } from '../platform/desktop';
import { MemeImage, resultText } from './Celebration';
import { Confetti } from './Confetti';
import { Icon } from './Icon';
import { cls } from './NewTaskForm';
import { TimerText } from './TimerText';
import { Tomato } from './Tomato';

/** Quanto o aviso "Iniciada" fica na barra (ASM-095) e quanto dura a saída para cima. */
export const STARTED_FLASH_MS = 2000;
export const STARTED_EXIT_MS = 320;

/** 'in' = visível · 'out' = saindo para cima · null = escondido. Dispara quando uma tarefa começa a rodar. */
function useStartedFlash(runningId: string | null): 'in' | 'out' | null {
  const [flash, setFlash] = useState<'in' | 'out' | null>(null);
  const last = useRef<string | null>(null);
  useEffect(() => {
    const prev = last.current;
    last.current = runningId;
    if (!runningId || runningId === prev) return;
    setFlash('in');
    const out = setTimeout(() => setFlash('out'), STARTED_FLASH_MS);
    const end = setTimeout(() => setFlash(null), STARTED_FLASH_MS + STARTED_EXIT_MS);
    return () => {
      clearTimeout(out);
      clearTimeout(end);
      setFlash(null);
    };
  }, [runningId]);
  return flash;
}

interface Props {
  snapshot: FloatSnapshot;
  now: number;
  onCommand(cmd: FloatCommand): void;
}

export function FloatBar({ snapshot, now, onCommand }: Props) {
  const { t } = useTranslation();
  const [hover, setHover] = useState(false);
  const { task, pomodoro, celebrating, offline } = snapshot;
  const onBreak = !!pomodoro && (pomodoro.phase === 'break' || pomodoro.phase === 'breakOver');
  const started = useStartedFlash(task?.status === 'running' && !celebrating && !onBreak ? task.id : null);
  const ready = pomodoro?.phase === 'ready';
  // comemoração com meme; desligada (quiet), a barra não cresce
  const loud = celebrating && !celebrating.quiet ? celebrating : null;
  // a comemoração continua desenhada enquanto sai para a esquerda
  const lastCelebration = useRef(loud);
  if (loud) lastCelebration.current = loud;
  else if (!ready) lastCelebration.current = null;
  const hoverProps = {
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false),
  };

  if (celebrating?.quiet && !ready) {
    // "Comemoração" desligada: "Feito!" verde no lugar do título, como o "Iniciada" (AC-230)
    return (
      <div className="float" data-tauri-drag-region data-testid="float-bar">
        <Icon name="check" size={15} className="float__check" />
        <span className="float__head" data-tauri-drag-region>
          <span className="float__title float__done-flash" role="status" data-tauri-drag-region>
            {t('float.done')}
          </span>
        </span>
      </div>
    );
  }

  if (loud || ready) {
    // a janela cresce para 300 × 260 durante a comemoração e a hora de relaxar (AC-046 / AC-189)
    const shown = loud ?? lastCelebration.current;
    return (
      <div className="float float--celebrate" data-tauri-drag-region data-testid="float-bar" role={loud ? 'status' : undefined}>
        <div className={cls('float__track', !loud && 'float__track--relax')} data-tauri-drag-region>
          <div className="float__pane" aria-hidden={!loud} data-tauri-drag-region>
            {shown && (
              <>
                {loud && <Confetti pieces={40} />}
                <span className="float__celebrate-top">
                  <Icon name="check" size={15} className="float__check" />
                  <s className="float__title" title={shown.title}>
                    {shown.title}
                  </s>
                </span>
                <strong className="float__done">{t('float.done')}</strong>
                <MemeImage id={shown.meme} className="float__meme" />
                <span className="float__result">{resultText(shown.result, t)}</span>
              </>
            )}
          </div>
          {ready && (
            <div className="float__pane float__relax" data-testid="float-relax" data-tauri-drag-region>
              <Tomato size={84} relaxed />
              <strong className="float__done">{t('pomodoro.relaxTitle')}</strong>
              <span className="float__result">{t('pomodoro.relaxBody', { duration: formatDuration(pomodoro!.breakSec, t) })}</span>
              <button type="button" className="btn btn--primary btn--sm" onClick={() => onCommand({ type: 'relax' })}>
                {t('pomodoro.relaxNow')}
              </button>
              <button type="button" className="float__skip" onClick={() => onCommand({ type: 'resume', id: task?.id ?? '' })}>
                {t('pomodoro.skipRest')}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (pomodoro && onBreak) {
    const over = pomodoro.phase === 'breakOver';
    return (
      <div className="float float--break" data-tauri-drag-region data-testid="float-bar" {...hoverProps}>
        <Icon name="coffee" size={16} />
        <span className="float__title" data-tauri-drag-region>
          {over ? t('pomodoro.backToFocusTitle') : t('float.break')}
        </span>
        {!over && <span className="timer timer--break">{pomodoroView(pomodoro, now).text}</span>}
        {over || hover ? (
          <button type="button" className="icon-btn icon-btn--sm" aria-label={t('pomodoro.resume')} onClick={() => onCommand({ type: 'resume', id: task?.id ?? '' })}>
            <Icon name="play" size={14} filled />
          </button>
        ) : (
          <button type="button" className="icon-btn icon-btn--sm" aria-label={t('pomodoro.skipBreak')} onClick={() => onCommand({ type: 'skipBreak' })}>
            <Icon name="skip" size={14} />
          </button>
        )}
      </div>
    );
  }

  if (!task) return <div className="float float--empty" data-tauri-drag-region data-testid="float-bar" />;

  const running = task.status === 'running';
  const focusBlock = pomodoro && pomodoro.phase === 'focus' && pomodoro.taskId === task.id;
  const view = timerView(task, now);

  return (
    <div className={cls('float', hover && 'float--hover', !running && 'float--paused')} data-tauri-drag-region data-testid="float-bar" {...hoverProps}>
      {offline && <span className="float__offline" role="img" aria-label={t('offline.short')} />}
      {!running && <Icon name="pause" size={13} className="float__paused-icon" />}
      <span className="float__head" data-tauri-drag-region>
        <span className="float__title" title={task.title} data-tauri-drag-region>
          {task.title}
        </span>
        {started && (
          <span className={cls('float__started', started === 'out' && 'float__started--out')} role="status" data-tauri-drag-region>
            {t('float.started')}
          </span>
        )}
      </span>
      {focusBlock ? <span className="timer timer--focus">{pomodoroView(pomodoro!, now).text}</span> : <TimerText view={view} />}
      {hover && (
        <span className="float__controls">
          {running ? (
            <button type="button" className="icon-btn icon-btn--sm" aria-label={t('current.pause')} disabled={offline} onClick={() => onCommand({ type: 'pause', id: task.id })}>
              <Icon name="pause" size={15} />
            </button>
          ) : (
            <button type="button" className="icon-btn icon-btn--sm" aria-label={t('current.resume')} disabled={offline} onClick={() => onCommand({ type: 'resume', id: task.id })}>
              <Icon name="play" size={13} filled />
            </button>
          )}
          <button type="button" className="icon-btn icon-btn--sm icon-btn--green" aria-label={t('current.finish')} disabled={offline} onClick={() => onCommand({ type: 'finish', id: task.id })}>
            <Icon name="check" size={16} />
          </button>
          <button type="button" className="icon-btn icon-btn--sm" aria-label={t('float.open')} onClick={() => onCommand({ type: 'open' })}>
            <Icon name="expand" size={14} />
          </button>
        </span>
      )}
    </div>
  );
}
